import { ValueOf } from 'core/utils/types';

export const PropertyMode = {
  default: 'rw',
  readonly: 'readonly',
  init: 'init',
} as const;
export type PropertyMode = ValueOf<typeof PropertyMode>;

export interface PropertyOptions {
  /**
   * Mode d'accès au setter :
   * - 'rw' (Read/Write) : Comportement par défaut { get; set; }
   * - 'readonly' : Setter désactivé après initialisation { get; }
   * - 'init' : Assignable une seule fois (ou null -> value) { get; init; }
   * @default 'rw'
   */
  mode?: PropertyMode;

  /**
   * Déclenche _p_update() lors du changement de valeur ?
   * @default false
   */
  reactive?: boolean;
}

/**
 * @Property Gère la réactivité et les droits d'accès des Auto-Accessors.
 * Simule les comportements C# { get; set; }, { get; init; } et { get; }.
 */
export function Property(options: PropertyOptions = {}) {
  const { mode = PropertyMode.default, reactive = false } = options;

  return function <This extends any, Value>(
    target: ClassAccessorDecoratorTarget<This, Value>,
    context: ClassAccessorDecoratorContext<This, Value>,
  ): ClassAccessorDecoratorResult<This, Value> {
    const name = String(context.name);

    return {
      // Init: Appelé lors de l'initialisation de la classe (ex: accessor x = 10)
      init(initialValue: Value): Value {
        return initialValue;
      },

      // Get: Lecture standard via le backing field natif
      get(this: This): Value {
        return target.get.call(this);
      },

      // Set: Logique de protection et réactivité
      set(this: This, newValue: Value) {
        const oldValue = target.get.call(this);

        // 1. Gestion des Modes (Runtime Security)
        if (mode === 'readonly') {
          // Note: L'initialisation via "accessor x = val" passe par init(), pas set().
          // Donc ici, c'est une tentative de modification ultérieure.
          throw new Error(`[Property] '${name}' is ReadOnly ({ get; }).`);
        }

        if (mode === 'init') {
          // Pattern { get; init; }
          // On autorise si la valeur actuelle est undefined/null (premier set)
          // Ou si on est techniquement encore dans la phase de construction (dur à détecter parfaitement en JS pur sans état,
          // mais on suppose que si oldValue existe, c'est trop tard).
          if (oldValue !== undefined && oldValue !== null) {
            throw new Error(
              `[Property] '${name}' is InitOnly ({ get; init; }).`,
            );
          }
        }

        // 2. Optimisation : Pas de changement, pas d'event
        if (oldValue === newValue) return;

        // 3. Mise à jour du backing field natif
        target.set.call(this, newValue);

        // 4. Réactivité (Appel du moteur de rendu Bnum)
        if (reactive && typeof (this as any)._p_update === 'function') {
          (this as any)._p_update();
        }
      },
    };
  };
}
